import firebase from 'firebase';
import 'firebase/analytics';

import { getAnalyticsContext } from './context';
import type { AnalyticsEventName } from './events';

type AnalyticsValue = string | number | boolean | undefined | null;
type AnalyticsParams = Record<string, AnalyticsValue>;

let analyticsInitialized = false;
let analyticsAvailable = false;

const isAnalyticsEnabled = (): boolean =>
  process.env.REACT_APP_ENABLE_ANALYTICS === 'true';

const toAnalyticsParams = (
  params: AnalyticsParams,
): Record<string, string | number> =>
  Object.entries(params).reduce<Record<string, string | number>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc;
      }

      acc[key] = typeof value === 'boolean' ? +value : value;
      return acc;
    },
    {},
  );

export const initAnalytics = async (): Promise<void> => {
  if (analyticsInitialized || !isAnalyticsEnabled()) {
    return;
  }

  analyticsInitialized = true;

  try {
    if (!firebase.analytics || typeof window === 'undefined') {
      return;
    }

    const isSupported = await firebase.analytics.isSupported();
    if (!isSupported) {
      return;
    }

    firebase.analytics();
    analyticsAvailable = true;
  } catch (error) {
    console.error('Firebase analytics initialization failed', error);
  }
};

export const trackEvent = (
  eventName: AnalyticsEventName,
  params: AnalyticsParams = {},
): void => {
  if (!analyticsAvailable || !isAnalyticsEnabled()) {
    return;
  }

  try {
    const analytics = firebase.analytics();
    analytics.logEvent(eventName, {
      ...toAnalyticsParams(getAnalyticsContext()),
      ...toAnalyticsParams(params),
    });
  } catch (error) {
    console.error('Firebase analytics event failed', eventName, error);
  }
};
